import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { categoriesApi } from '@/services/articles';
import { destinationsApi } from '@/services/config';
import {
  intelligenceService,
  type QuentesConfig,
  type QuentesRunResult,
} from '@/services/intelligence';
import { usePlatformAccess } from '@/services/platform';

import styles from './ConfigQuentes.module.css';

// Estilo inline (só tokens, sem cor literal) porque o módulo CSS desta tela não
// tem classe de aviso — as demais telas usam `.aviso`, que é local a cada uma.
const AVISO_LEITURA: React.CSSProperties = {
  margin: 0,
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-2)',
  borderLeft: '2px solid var(--warn)',
  borderRadius: 'var(--r-sm)',
  color: 'var(--text-3)',
  fontSize: 'var(--fs-xs)',
  lineHeight: 'var(--lh-normal)',
  maxWidth: '78ch',
};

/**
 * POR QUE: o backend já entregou `skipped` como STRING nos ciclos pulados
 * (F-251) e `.map()` numa string estoura TypeError — o ErrorBoundary engolia a
 * rota inteira e o operador via "Página não encontrada". A lista só é iterada
 * depois de provada lista.
 */
function listaDePulos(bruto: unknown): { tema: string; motivo: string }[] {
  return Array.isArray(bruto) ? (bruto as { tema: string; motivo: string }[]) : [];
}

const EDITORIA_ORDER = [
  'Esportes',
  'Economia',
  'Mundo',
  'Tempo',
  'Tecnologia',
  'Entretenimento',
  'Saúde',
];

export function ConfigQuentes() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<QuentesConfig | null>(null);
  const [runResult, setRunResult] = useState<QuentesRunResult | null>(null);
  // "Rodar agora" gera VÁRIOS rascunhos de uma vez — confirma em 2 cliques.
  const [armed, setArmed] = useState(false);

  // PUT /quentes/config e POST /quentes/run são require_staff no backend desde
  // f734f53 (a esteira publica nas marcas da casa). O GET não é: por isso a
  // leitura continua para todo mundo e só a escrita fica trancada aqui.
  const acesso = usePlatformAccess();
  const podeEscrever = acesso.isStaff;
  // Enquanto a sonda de staff não responde, ninguém escreve — negar por
  // omissão evita oferecer um botão que o backend vai recusar.
  const sondandoAcesso = acesso.query.isLoading;
  const somenteLeitura = !podeEscrever && !sondandoAcesso;

  const { data, isLoading } = useQuery({
    queryKey: ['quentes-config'],
    queryFn: intelligenceService.getQuentesConfig,
  });
  const { data: categories } = useQuery({
    queryKey: ['wp-categories'],
    queryFn: () => categoriesApi.list(),
  });
  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 300_000,
  });

  useEffect(() => {
    if (data) setCfg(structuredClone(data));
  }, [data]);

  const save = useMutation({
    mutationFn: (c: QuentesConfig) => intelligenceService.saveQuentesConfig(c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quentes-config'] }),
  });
  const run = useMutation({
    mutationFn: () => intelligenceService.runQuentes(),
    onSuccess: (r) => { setRunResult(r); setArmed(false); },
    onError: () => setArmed(false),
  });

  if (isLoading || !cfg) {
    return <section className={styles.root}><p className={styles.muted}>Carregando…</p></section>;
  }

  const set = <K extends keyof QuentesConfig>(k: K, v: QuentesConfig[K]) =>
    setCfg({ ...cfg, [k]: v });

  const setEd = (
    ed: string,
    patch: Partial<QuentesConfig['editorias'][string]>,
  ) =>
    setCfg({
      ...cfg,
      editorias: {
        ...cfg.editorias,
        [ed]: { ...cfg.editorias[ed], ...patch },
      },
    });

  const isLive = cfg.publish_status === 'publish';

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Fluxo de Quentes</h1>
          <p className={styles.subtitle}>
            Esteira automática: rankeia tendências, enriquece com PAA e N fontes,
            escreve com IA e publica no WordPress na categoria da editoria. Corre
            sozinha a cada hora dentro da janela ativa.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant={armed ? 'primary' : 'secondary'}
            onClick={() => {
              if (!armed) {
                setArmed(true);
                return;
              }
              run.mutate();
            }}
            disabled={run.isPending || !podeEscrever}
            title={
              podeEscrever
                ? 'Roda o pipeline de Quentes e gera vários rascunhos das top tendências do momento'
                : 'A esteira de Quentes é operada pela casa neste plano.'
            }
          >
            {run.isPending
              ? 'A rodar…'
              : armed
                ? 'Confirmar — gera vários rascunhos'
                : 'Rodar lote agora'}
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate(cfg)}
            disabled={save.isPending || !podeEscrever}
            title={
              podeEscrever
                ? undefined
                : 'A esteira de Quentes é operada pela casa neste plano.'
            }
          >
            {save.isPending ? 'A gravar…' : 'Salvar'}
          </Button>
        </div>
      </header>

      {!cfg.enabled && (
        <p
          style={{
            color: 'var(--sig-warn)',
            fontSize: 13,
            border: '1px solid currentColor',
            borderRadius: 6,
            padding: '8px 12px',
          }}
        >
          Esteira desligada até você escolher um site e ligar.
        </p>
      )}

      {somenteLeitura && (
        <p role="status" style={AVISO_LEITURA}>
          A esteira de Quentes é operada pela casa neste plano — os valores abaixo
          são leitura.
        </p>
      )}

      {save.isSuccess && <p className={styles.ok}>Configuração gravada.</p>}
      {save.isError && (
        <p style={{ color: 'var(--sig-crit)', fontSize: 13 }}>
          Falha ao gravar: {detalheDoErro(save.error)}
        </p>
      )}
      {run.isError && (
        <p style={{ color: 'var(--sig-crit)', fontSize: 13 }}>
          Falha ao rodar o lote: {detalheDoErro(run.error)}
        </p>
      )}

      {/* Estado / segurança */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Estado</h2>
        <div className={styles.switches}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={cfg.enabled}
              disabled={!podeEscrever}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            <span>Esteira ativa</span>
          </label>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={cfg.kill_switch}
              disabled={!podeEscrever}
              onChange={(e) => set('kill_switch', e.target.checked)}
            />
            <span>Kill switch (trava tudo)</span>
          </label>
        </div>

        <Field
          label="Destino (site WordPress)"
          hint="Onde o Fluxo de Quentes publica. Obrigatório — sem isto o lote não roda (não há mais fallback para o 1º site)."
        >
          <select
            className={styles.select}
            value={cfg.wp_site ?? ''}
            disabled={!podeEscrever}
            onChange={(e) => set('wp_site', e.target.value || null)}
          >
            <option value="">— escolha o destino —</option>
            {(sites ?? []).map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <div className={`${styles.publishRow} ${isLive ? styles.publishLive : ''}`}>
          <div>
            <span className={styles.publishLabel}>Modo de publicação</span>
            <p className={styles.muted}>
              {isLive
                ? 'PUBLICADO — as matérias vão direto ao ar para os leitores.'
                : 'RASCUNHO — as matérias entram no WordPress como rascunho, invisíveis aos leitores. Seguro para testes.'}
            </p>
          </div>
          <div
            className={styles.segmented}
            style={podeEscrever ? undefined : { opacity: 0.6 }}
          >
            <button
              type="button"
              className={!isLive ? styles.segActive : styles.seg}
              disabled={!podeEscrever}
              onClick={() => set('publish_status', 'draft')}
            >
              Rascunho
            </button>
            <button
              type="button"
              className={isLive ? styles.segActiveLive : styles.seg}
              disabled={!podeEscrever}
              onClick={() => {
                if (
                  isLive ||
                  window.confirm(
                    'Mudar para "Publicado"? O Fluxo de Quentes passa a publicar AO VIVO no site, sem revisão.',
                  )
                )
                  set('publish_status', 'publish');
              }}
            >
              Publicado
            </button>
          </div>
        </div>
      </div>

      {/* Parâmetros globais */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Parâmetros</h2>
        <div className={styles.grid}>
          <Field label="Geografia" hint="BR ou BR-SP, BR-RJ…">
            <input
              className={styles.input}
              value={cfg.geo}
              disabled={!podeEscrever}
              onChange={(e) => set('geo', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Janela de tendências (horas)" hint="4, 24, 48, 168">
            <input
              type="number"
              className={styles.input}
              value={cfg.hours}
              disabled={!podeEscrever}
              onChange={(e) => set('hours', Number(e.target.value))}
            />
          </Field>
          <Field label="Corte de score" hint="Só produz se score ≥ este valor (0-100)">
            <input
              type="number"
              className={styles.input}
              value={cfg.score_cut}
              disabled={!podeEscrever}
              onChange={(e) => set('score_cut', Number(e.target.value))}
            />
          </Field>
          <Field label="Mínimo de fontes" hint="Guarda anti-boato: exige N veículos">
            <input
              type="number"
              className={styles.input}
              value={cfg.min_sources}
              disabled={!podeEscrever}
              onChange={(e) => set('min_sources', Number(e.target.value))}
            />
          </Field>
          <Field label="Máx. por ciclo" hint="Teto de matérias por execução">
            <input
              type="number"
              className={styles.input}
              value={cfg.max_per_cycle}
              disabled={!podeEscrever}
              onChange={(e) => set('max_per_cycle', Number(e.target.value))}
            />
          </Field>
          <Field label="Janela ativa" hint="Hora de início e fim (UTC)">
            <div className={styles.range}>
              <input
                type="number"
                className={styles.input}
                value={cfg.active_start}
                disabled={!podeEscrever}
                onChange={(e) => set('active_start', Number(e.target.value))}
              />
              <span className={styles.muted}>até</span>
              <input
                type="number"
                className={styles.input}
                value={cfg.active_end}
                disabled={!podeEscrever}
                onChange={(e) => set('active_end', Number(e.target.value))}
              />
            </div>
          </Field>
        </div>
      </div>

      {/* Editorias */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Editorias</h2>
        <p className={styles.muted}>
          Liga/desliga cada editoria, define o teto diário e mapeia para a
          categoria correspondente no WordPress.
        </p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Editoria</th>
              <th>Ativa</th>
              <th>Teto/dia</th>
              <th>Categoria no WordPress</th>
            </tr>
          </thead>
          <tbody>
            {EDITORIA_ORDER.filter((ed) => cfg.editorias[ed]).map((ed) => {
              const row = cfg.editorias[ed];
              return (
                <tr key={ed} className={row.enabled ? '' : styles.rowOff}>
                  <td className={styles.edName}>{ed}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled={!podeEscrever}
                      onChange={(e) => setEd(ed, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className={styles.inputSm}
                      value={row.cap_dia}
                      disabled={!podeEscrever}
                      onChange={(e) => setEd(ed, { cap_dia: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <select
                      className={styles.select}
                      value={row.wp_category_id ?? ''}
                      disabled={!podeEscrever}
                      onChange={(e) =>
                        setEd(ed, {
                          wp_category_id: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    >
                      <option value="">— sem categoria —</option>
                      {(categories ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resultado de "Rodar agora" */}
      {runResult && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Último ciclo</h2>
          {runResult.skipped_reason ? (
            <p className={styles.muted}>Ciclo pulado — {runResult.skipped_reason}.</p>
          ) : typeof runResult.ranked === 'number' ? (
            <p className={styles.muted}>
              {runResult.ranked} tendências rankeadas · {runResult.picked} escolhidas ·
              modo {runResult.publish_status}
            </p>
          ) : (
            <p className={styles.muted}>Ciclo pulado.</p>
          )}
          {(runResult.produced ?? []).map((p) => (
            <div key={p.wp_id} className={styles.resultRow}>
              <Badge tone={p.status === 'publish' ? 'crit' : 'neutral'}>
                {p.status === 'publish' ? 'PUBLICADO' : 'RASCUNHO'}
              </Badge>
              <span className={styles.edName}>{p.editoria}</span>
              <span>{p.tema}</span>
              <span className={styles.muted}>score {p.score} · {p.sources} fontes</span>
              <a href={p.wp_link} target="_blank" rel="noreferrer" className={styles.link}>
                ver no WP #{p.wp_id}
              </a>
            </div>
          ))}
          {listaDePulos(runResult.skipped).map((s, i) => (
            <div key={i} className={styles.skipRow}>
              <span className={styles.muted}>pulou:</span> {s.tema} — {s.motivo}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * O motivo real vem do backend em body.detail — `Error.message` do ApiError é
 * só "403 Forbidden", que veste recusa de permissão de falha da esteira.
 * detail chega como string OU como objeto {erro, motivo}.
 */
function detalheDoErro(err: unknown): string {
  const detail = (err as { body?: { detail?: unknown } } | null)?.body?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const d = detail as { erro?: unknown; motivo?: unknown };
    const partes = [d.erro, d.motivo].filter(
      (p): p is string => typeof p === 'string' && p.length > 0,
    );
    if (partes.length > 0) return partes.join(' — ');
    return JSON.stringify(detail).slice(0, 300);
  }
  return (err as Error)?.message || String(err);
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </label>
  );
}
